import React, { useRef, useState, useEffect } from 'react';
import Webcam from 'react-webcam';
import { Modal, Button } from 'react-bootstrap';
import { FaCheckSquare, FaRegSquare, FaCamera, FaSyncAlt, FaUpload, FaTimes } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { showToast } from 'Core/redux/toast/slice';
import { useDocumentUpload } from 'Core/hooks';
import { MOLD_TYPE } from '../../constants/moldConstants';
import { sanitizeUploadKey } from '../sanitizeUploadKeys/sanitizeUploadKeys';
import { MoldService } from '../../../service/api/mold-service';
import { CellService } from '../../../service/api/cell-service';
import LoadingSpinner from '../../../InitialPage/Sidebar/LoadingSpinner';
import ZoomableImageModal from '../zoomableimagemodal/ZoomableImageModal';
import './segment-webcam-multi-capture-modal.css';

const defaultSlots = [
  { key: 'general', label: 'General' },
  { key: 'segment1', label: 'Segment 1' },
];

const SegmentWebcamMultiCaptureModal = ({ show, onClose, onSubmit, toolID, moldInfoID, buttonLabel, maintenanceId = 0, onImageSegmentUploaded, maintenanceData, onMaintenanceSaved, onActivityIssueSaved, context = 'maintenance', onDraftIdUpdate, savedMaintenanceActivityData, issueData, categories }) => {
  console.log('SegmentWebcamMultiCaptureModal: onActivityIssueSaved callback:', onActivityIssueSaved);
  console.log('SegmentWebcamMultiCaptureModal: issueData:', issueData);
  console.log('SegmentWebcamMultiCaptureModal: context:', context);
  const dispatch = useDispatch();
  const { imageDocuments } = useDocumentUpload();
  
  // Debug modal state
  useEffect(() => {
    console.log('SegmentWebcamMultiCaptureModal: Modal show state:', show);
    if (show) {
      console.log('Modal opened - current slotImages:', slotImages);
      console.log('Modal opened - current slots:', slots);
    }
  }, [show]);

  const webcamRef = useRef(null);
  //const [images, setImages] = useState([]); // [{ id, src, isSelected }]
  const [facingMode, setFacingMode] = useState('environment'); // Default to back camera
  const [slots, setSlots] = useState(defaultSlots);
  const [slotImages, setSlotImages] = useState({});
  const [activeSlot, setActiveSlot] = useState(defaultSlots[0].key);
  const [isLoading, setIsLoading] = useState(false);
  const [isCameraLoading, setIsCameraLoading] = useState(false);
  const [uploadedImageUrls, setUploadedImageUrls] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingImages, setPendingImages] = useState([]);
  const [showUploadArea, setShowUploadArea] = useState(false);
  
  // Image editor state
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [editingSlot, setEditingSlot] = useState(null);
  const [isProcessingImages, setIsProcessingImages] = useState(false);
  const [processingProgress, setProcessingProgress] = useState({ current: 0, total: 0 });

  const videoConstraints = {
    facingMode: facingMode,
  };


  //* Capture image into active slot
  const captureImage = async () => {
    if (!activeSlot) return;
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
      const id = `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      console.log('Capturing image for slot:', activeSlot, 'with id:', id);
      setSlotImages(prev => {
        const arr = prev[activeSlot] || [];
        const newImages = [...arr, { id, src: imageSrc, isSelected: true }];
        console.log('Updated slotImages for', activeSlot, ':', newImages);
        return { ...prev, [activeSlot]: newImages };
      });
    } else {
      console.log('Failed to capture image - no screenshot available');
    }
  };

  //*new
  const toggleSelect = (slotKey, id) => {
    setSlotImages(prev => ({
      ...prev,
      [slotKey]: (prev[slotKey] || []).map(img =>
        img.id === id ? { ...img, isSelected: !img.isSelected } : img,
      ),
    }));
  };

  // Handle file selection for active slot
  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Filter image files (including HEIC/HEIF)
    const imageFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isHeic = file.name.toLowerCase().endsWith('.heic') || 
                     file.name.toLowerCase().endsWith('.heif');
      return isImage || isHeic;
    });

    if (imageFiles.length !== files.length) {
      dispatch(showToast({ 
        message: 'Only image files (including HEIC) are allowed', 
        type: 'warning' 
      }));
    }

    if (imageFiles.length > 0) {
      let processedCount = 0;
      const totalFiles = imageFiles.length;
      const errors = [];
      const processedFiles = [];

      // Set loading state
      setIsProcessingImages(true);
      setProcessingProgress({ current: 0, total: totalFiles });

      // Process each file (convert HEIC and compress)
      for (let i = 0; i < imageFiles.length; i++) {
        const file = imageFiles[i];
        setProcessingProgress({ current: i + 1, total: totalFiles });

        try {
          // Import image processor utilities
          const { processImageFile } = await import('Core/utils/imageProcessor');
          
          // Process the file (convert HEIC if needed, then compress)
          const processedFile = await processImageFile(file, {
            maxSizeMB: 2, // Maximum 2MB per file
            maxWidthOrHeight: 1920, // Maximum dimension
            quality: 0.85 // Good quality balance
          });

          // Create object URL for preview
          const src = URL.createObjectURL(processedFile);
          
          processedFiles.push({
            id: `upload_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
            file: processedFile, // Use processed file instead of original
            name: processedFile.name,
            src: src,
            isSelected: true,
            isUploaded: true, // Flag to distinguish from captured images
            originalFileName: file.name // Keep original name for reference
          });

          processedCount++;
          } catch (error) {
          console.error('Error processing file:', file.name, error);
          errors.push(file.name);
          
          // If processing fails, fall back to using the original file
          // but mark it as NOT pre-uploaded so that the upload step
          // will always convert it to a JPEG file (same behavior as
          // the Mold Receiving webcam modal).
          try {
            const src = URL.createObjectURL(file);
            processedFiles.push({
              id:     `upload_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
              // keep reference to original file for debugging if needed,
              // but force the upload logic to treat it like a captured image
              file: file,
              name: file.name,
              src: src,
              isSelected: true,
              isUploaded: false
            });
            processedCount++;
          } catch (fallbackError) {
            console.error('Error creating object URL for file:', file.name, fallbackError);
            errors.push(file.name);
            processedCount++;
          }
        }

        // Update UI after all files are processed
        if (processedCount === totalFiles) {
          setIsProcessingImages(false);
          setProcessingProgress({ current: 0, total: 0 });

          if (processedFiles.length > 0) {
            setSlotImages(prev => {
              const currentImages = prev[activeSlot] || [];
              return { ...prev, [activeSlot]: [...currentImages, ...processedFiles] };
            });

            // Auto-close upload area after successful upload
            setShowUploadArea(false);
            const successMessage = errors.length > 0 
              ? `${processedFiles.length} image(s) uploaded, ${errors.length} failed`
              : `${totalFiles} image(s) uploaded successfully!`;
            dispatch(showToast({ 
              message: successMessage, 
              type: errors.length > 0 ? 'warning' : 'success' 
            }));
          } else {
            dispatch(showToast({ 
              message: 'Failed to process images', 
              type: 'danger' 
            }));
          }
        }
      }
    }

    // Reset the input
    event.target.value = '';
  };

  // Remove image from slot
  const removeImage = (slotKey, id) => {
    setSlotImages(prev => ({
      ...prev,
      [slotKey]: (prev[slotKey] || []).filter(img => {
        if (img.id === id && img.isUploaded && img.src) {
          // Clean up object URL for uploaded files
          URL.revokeObjectURL(img.src);
        }
        return img.id !== id;
      })
    }));
  };

  // Open image editor
  const handleEditImage = (slotKey, img) => {
    setEditingSlot(slotKey);
    setEditingImage(img);
    setShowImageEditor(true);
  };

  // Save edited image
  const handleSaveEditedImage = async (editedBlob) => {
    if (!editingImage || !editingSlot) return;

    try {
      // Convert blob to base64 data URL
      const reader = new FileReader();
      reader.onload = (e) => {
        const editedSrc = e.target.result;
        
        // Update the image in slotImages
        setSlotImages(prev => ({
          ...prev,
          [editingSlot]: (prev[editingSlot] || []).map(img =>
            img.id === editingImage.id
              ? { ...img, src: editedSrc, isEdited: true }
              : img
          )
        }));
        
        dispatch(showToast({ 
          message: 'Image edited successfully!', 
          type: 'success' 
        }));
        
        // Close the editor
        setShowImageEditor(false);
        setEditingImage(null);
        setEditingSlot(null);
      };
      reader.readAsDataURL(editedBlob);
    } catch (error) {
      console.error('Error saving edited image:', error);
      dispatch(showToast({ 
        message: 'Failed to save edited image', 
        type: 'danger' 
      }));
    }
  };

  // Handle drag and drop events
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.style.borderColor = '#28a745';
    e.currentTarget.style.backgroundColor = '#f0fff4';
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.style.borderColor = '#dee2e6';
    e.currentTarget.style.backgroundColor = '#fafafa';
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.style.borderColor = '#dee2e6';
    e.currentTarget.style.backgroundColor = '#fafafa';
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    // Create a synthetic event object to reuse handleFileSelect logic
    const event = { target: { files } };
    await handleFileSelect(event);
  };

  const flipCamera = async () => {
    setIsCameraLoading(true);
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter(device => device.kind === 'videoinput');

      if (videoInputs.length < 2) {
        dispatch(
          showToast({ message: 'No back camera or external webcam available', type: 'warning' }),
        );
        return;
      }

      setFacingMode(prev => (prev === 'user' ? 'environment' : 'user'));
    } catch (err) {
      console.error('Error checking video devices:', err);
      dispatch(showToast({ message: 'Unable to access video devices', type: 'danger' }));
    } finally {
      setIsCameraLoading(false);
    }
  };

  // Add new custom slot
  const addSlot = () => {
    const newKey = `slot_${Date.now()}`;
    setSlots(prev => [...prev, { key: newKey, label: 'New Segment' }]);
    setActiveSlot(newKey);
  };
  // Handle submit - Always show confirmation dialog for maintenance save
  const handleSubmit = () => {
    // Collect all selected images first
    const selectedImages = [];
    for (const slot of slots) {
      const images = (slotImages[slot.key] || []).filter(i => i.isSelected);
      console.log(`Slot ${slot.key} has ${images.length} selected images:`, images);
      for (const img of images) {
        selectedImages.push({
          slot,
          img,
          slotKey: slot.key
        });
      }
    }
    
    console.log('handleSubmit called');
    console.log('selectedImages length:', selectedImages.length);
    console.log('selectedImages:', selectedImages);
    console.log('slotImages:', slotImages);
    console.log('slots:', slots);
    
    if (selectedImages.length === 0) {
      dispatch(showToast({ message: 'No images selected', type: 'warning' }));
      return;
    }
    
    setPendingImages(selectedImages);
    
    // Skip confirmation dialog for Add Actions (maintenance context with Action Images buttonLabel)
    if (context === 'maintenance' && buttonLabel === 'Action Images') {
      console.log('Skipping confirmation dialog for Add Actions - calling APIs directly');
      handleConfirmSubmit(selectedImages);
      return;
    }
    
    // Always show confirmation dialog for other contexts
    setShowConfirmDialog(true);
  };

  // Handle confirmation and proceed with upload
  const handleConfirmSubmit = async (imagesToUpload = null) => {
    // Use passed images or fall back to pendingImages state
    const imagesToProcess = imagesToUpload || pendingImages;
    
    console.log('handleConfirmSubmit called');
    console.log('imagesToProcess:', imagesToProcess);
    console.log('pendingImages in handleConfirmSubmit:', pendingImages);
    
    setShowConfirmDialog(false);
    setIsLoading(true);
    
    try {
      // Handle special case for "other-maintenance-issue" context
      if (context === 'other-maintenance-issue' && issueData) {
        console.log('Processing other-maintenance-issue context with issueData:', issueData);
        
        // First, save the maintenance activity issue
        let activityIssueId = 0;
        
        try {
          // Find the selected category to get CategoryId (following the same pattern as Save button)
          const selectedCategory = categories?.find(cat => cat.category === issueData.title);
          const categoryId = selectedCategory ? selectedCategory.categoryId : null;
          
          const issuePayload = {
            activityIssueId: issueData.activityIssueId || 0, // Use existing activityIssueId or 0 for new issue
            moldInfoID: moldInfoID || maintenanceData?.moldInfoID || maintenanceData?.moldNumber, // Use prop first, then maintenanceData source
            maintenanceId: maintenanceId,
            title: issueData.title || '', // Selected dropdown value
            categoryId: categoryId, // CategoryId from selected dropdown
            futureAction: issueData.futureActions || '', // Future Action text field
            comments: issueData.comments || '',
            subActivityIssues: (() => {
              // Deduplicate subIssues before sending to API to prevent duplicate records
              const subIssuesToSave = issueData.subIssues || [];
              const seenSubActivityIds = new Set();
              const seenContentKeys = new Set();
              const deduplicatedSubIssues = subIssuesToSave.filter(subIssue => {
                // If sub-issue has a valid subActivityId, deduplicate by ID
                const subActivityId = subIssue.subActivityId || subIssue.subActivityIssueId;
                if (subActivityId && subActivityId !== 0) {
                  if (seenSubActivityIds.has(subActivityId)) {
                    return false;
                  }
                  seenSubActivityIds.add(subActivityId);
                  return true;
                }
                
                // If no valid subActivityId, deduplicate by title + comments
                const contentKey = `${subIssue.title || ''}_${subIssue.comments || ''}`;
                if (seenContentKeys.has(contentKey)) {
                  return false;
                }
                seenContentKeys.add(contentKey);
                return true;
              });
              
              // Map to API format, using actual subActivityIssueId if available
              return deduplicatedSubIssues.map(subIssue => ({
                subActivityIssueId: subIssue.subActivityId || subIssue.subActivityIssueId || 0, // Use actual ID if available
                title: subIssue.title,
                comments: subIssue.comments
              }));
            })()
          };
          
          console.log('Calling saveOrUpdateMoldMaintenanceActivityIssue with payload:', issuePayload);
          const issueResponse = await MoldService.saveOrUpdateMoldMaintenanceActivityIssue(issuePayload);
          console.log('saveOrUpdateMoldMaintenanceActivityIssue response:', issueResponse);
          
          if (issueResponse && issueResponse.status === 200 && issueResponse.response?.activityIssueId) {
            activityIssueId = issueResponse.response.activityIssueId;
            console.log('Activity issue saved with ID:', activityIssueId);
            
            // Notify parent component about the saved activityIssueId
            if (onActivityIssueSaved && typeof onActivityIssueSaved === 'function') {
              console.log('=== CALLING onActivityIssueSaved CALLBACK ===');
              console.log('Calling onActivityIssueSaved with:', { activityIssueId, issueId: issueData.id });
              onActivityIssueSaved(activityIssueId, issueData.id);
              console.log('onActivityIssueSaved callback called successfully');
            } else {
              console.log('onActivityIssueSaved callback not available or not a function:', onActivityIssueSaved);
            }
          } else {
            throw new Error('Failed to save maintenance activity issue');
          }
        } catch (error) {
          console.error('Error saving maintenance activity issue:', error);
          dispatch(showToast({ 
            message: 'Failed to save maintenance activity issue: ' + error.message, 
            type: 'danger' 
          }));
          return; // Exit early if issue save fails
        }
        
        // Now proceed with image upload and saveOrUpdateMoldImageSegments for this specific context
        console.log('Processing images for other-maintenance-issue context');
        console.log('imagesToProcess:', imagesToProcess);
        console.log('imagesToProcess length:', imagesToProcess.length);
        
        if (imagesToProcess.length === 0) {
          console.log('No images to process, showing error');
          dispatch(showToast({ 
            message: 'No images selected to upload', 
            type: 'warning' 
          }));
          setIsLoading(false);
          return;
        }
        
        const uploadedFiles = [];
        
        for (const { slot, img } of imagesToProcess) {
          try {
            console.log('Processing image for slot:', slot.label);
            let file;
            
            // Always convert to JPEG file (same behavior as Mold Receiving)
            // This ensures HEIC files are properly converted even if processing succeeded
            // but the file type wasn't properly set, or if processing failed
            const response = await fetch(img.src);
            const blob = await response.blob();
            file = new File([blob], `image_${Date.now()}.jpg`, { type: 'image/jpeg' });
            
            console.log('Uploading file:', file);
            console.log('File details:', { name: file.name, size: file.size, type: file.type });
            
            // Create FormData for upload (following the same pattern as the regular flow)
            const fileCategoryId = sanitizeUploadKey(MOLD_TYPE.WEBCAM_CAPTURED_IMAGE);
            // Determine upload category based on context
            let uploadCategory;
            if (context === 'receiving') {
              uploadCategory = sanitizeUploadKey(MOLD_TYPE.MOLD_RECEIVING);
            } else if (context === 'release') {
              uploadCategory = sanitizeUploadKey(MOLD_TYPE.MOLD_RELEASE);
            } else {
              // Default to MOLD_MAINTENANCE for maintenance, maintenance-records, other-maintenance-issue, and all other contexts
              uploadCategory = sanitizeUploadKey(MOLD_TYPE.MOLD_MAINTENANCE);
            }
            
            const docFormData = new FormData();
            docFormData.append('file', file);
            docFormData.append('fileCategoryId', fileCategoryId);
            docFormData.append('uploadCategory', uploadCategory);
            
            const result = await imageDocuments(docFormData);
            console.log('Upload result:', result);
            console.log('Upload successful, result.relativePath:', result.relativePath);
            
            uploadedFiles.push({
              fileName: result.fileName,
              relativePath: result.relativePath,
              filePath: result.relativePath,
              slotName: slot.label,
            });
            
            // Call saveOrUpdateMoldImageSegments API with specific parameters for other-maintenance-issue
            try {
              const segmentData = {
                moldInfoID: moldInfoID,
                imageUrl: result.relativePath,
                segment: slot.label,
                record: 'Action Images', // Fixed as per requirement
                maintenanceId: maintenanceId,
                title: '',
                description: '',
                category: 'Other-Maintenance-Records-Actions', // Fixed as per requirement
                actionImageId: activityIssueId, // Use the activityIssueId from the API response
                subActionImageId: 0 // Set to 0 as per requirement
              };
              
              console.log('Using activityIssueId in saveOrUpdateMoldImageSegments:', activityIssueId);
              
              console.log('Calling saveOrUpdateMoldImageSegments with segmentData:', segmentData);
              const segmentResponse = await MoldService.saveUpdateImageSegments(segmentData);
              console.log('saveOrUpdateMoldImageSegments response:', segmentResponse);
              console.log('Image segment saved successfully for slot:', slot.label);
            
              // Notify parent about uploaded image segment
              if (onImageSegmentUploaded && typeof onImageSegmentUploaded === 'function') {
                const savedSegment = {
                  ...segmentData,
                  ...(segmentResponse?.response || {}),
                };
                if (!savedSegment.record) {
                  savedSegment.record = segmentData.record;
                }
                if (!savedSegment.category) {
                  savedSegment.category = segmentData.category;
                }
                onImageSegmentUploaded(savedSegment, savedSegment.record);
              }
            } catch (segmentError) {
              console.error('Error saving image segment:', segmentError);
              // Don't fail the entire upload process if segment saving fails
            }
          } catch (error) {
            console.error('Error processing image:', error);
            // Continue with other images even if one fails
          }
        }
        
        // Show success message and close modal only if images were processed
        if (uploadedFiles.length > 0) {
          console.log('Successfully processed', uploadedFiles.length, 'images');
          dispatch(showToast({ 
            message: 'Images uploaded successfully', 
            type: 'success' 
          }));
        } else {
          console.log('No images were successfully processed');
          dispatch(showToast({ 
            message: 'No images were uploaded', 
            type: 'warning' 
          }));
        }
        
        console.log('Closing modal - final uploadedFiles:', uploadedFiles);
        setSlotImages({});
        setActiveSlot(defaultSlots[0].key);
        setUploadedImageUrls([]);
        setShowUploadArea(false);
        
        if (onSubmit && typeof onSubmit === 'function') {
          console.log('Calling onSubmit with uploadedFiles:', uploadedFiles);
          onSubmit(uploadedFiles);
        }
        
        onClose();
        return; // Exit early for other-maintenance-issue context
      }
      
      // Regular flow for other contexts
      // First, save the maintenance if needed
      let currentMaintenanceId = maintenanceId;
      
      if (!currentMaintenanceId || currentMaintenanceId === 0) {
        console.log('No maintenanceId, calling saveUpdateMaintenance API to save maintenance first');
        console.log('Maintenance data being sent:', maintenanceData);
        
        try {
          // Call saveUpdateMaintenance API directly (following the same pattern as Save button)
          const response = await MoldService.saveUpdateMaintenance(maintenanceData);
          console.log('Maintenance save response:', response);
          
          if (response && (response.response || response.ID || response.id)) {
            currentMaintenanceId = response.ID || response.id || response.response?.maintenanceID || response.response?.id;
            console.log('Maintenance saved with ID:', currentMaintenanceId);
            
            // Notify parent component about the saved maintenance
            if (onMaintenanceSaved && typeof onMaintenanceSaved === 'function') {
              onMaintenanceSaved(currentMaintenanceId, maintenanceData.moldInfoID);
            }
          } else {
            throw new Error('Failed to save maintenance');
          }
        } catch (error) {
          console.error('Error saving maintenance:', error);
          dispatch(showToast({ 
            message: 'Failed to save maintenance: ' + error.message, 
            type: 'danger' 
          }));
          return; // Exit early if maintenance save fails
        }
      }
      
      // Now proceed with image upload and saveOrUpdateMoldImageSegments
      const uploadedFiles = [];

      for (const { slot, img } of imagesToProcess) {
        try {
          console.log('Processing image for slot:', slot.label);
          let file;
          
          // Always convert to JPEG file (same behavior as Mold Receiving)
          // This ensures HEIC files are properly converted even if processing succeeded
          // but the file type wasn't properly set, or if processing failed
          const response = await fetch(img.src);
          const blob = await response.blob();
          const safeTimestamp = new Date().toISOString().replace(/[:.]/g, '-');
          file = new File([blob], `${slot.label}_${safeTimestamp}.jpg`, {
            type: 'image/jpeg',
          });

          const fileCategoryId = sanitizeUploadKey(MOLD_TYPE.WEBCAM_CAPTURED_IMAGE);
          // Determine upload category based on context
          let uploadCategory;
          if (context === 'receiving') {
            uploadCategory = sanitizeUploadKey(MOLD_TYPE.MOLD_RECEIVING);
          } else if (context === 'release') {
            uploadCategory = sanitizeUploadKey(MOLD_TYPE.MOLD_RELEASE);
          } else {
            // Default to MOLD_MAINTENANCE for maintenance, maintenance-records, and all other contexts
            uploadCategory = sanitizeUploadKey(MOLD_TYPE.MOLD_MAINTENANCE);
          }
         
          const docFormData = new FormData();
          docFormData.append('file', file);
          docFormData.append('fileCategoryId', fileCategoryId);
          docFormData.append('uploadCategory', uploadCategory);

          const result = await imageDocuments(docFormData);

          if (result && result.relativePath) {
            uploadedFiles.push({
              fileName: file.name,
              relativePath: result.relativePath,
              filePath: result.relativePath,
              slotName: slot.label,
            });

            // Call saveOrUpdateMoldImageSegments API with ActionImageId=0, SubActionImageId=0
            try {
              // Determine record and category based on context
              let record, category;
              
              // Check if this is from documentation section (buttonLabel contains specific maintenance picture types)
              const isDocumentationSection = buttonLabel && (
                buttonLabel.includes('Maintenance Pictures') || 
                buttonLabel.includes('Before Maintenance') || 
                buttonLabel.includes('After Maintenance') ||
                buttonLabel.includes('Repair Pictures') ||
                buttonLabel.includes('Modification Pictures') ||
                buttonLabel === 'Additional Pictures' ||
                buttonLabel === 'Extra Images'
              );
              
              if (isDocumentationSection) {
                // For documentation section: use buttonLabel as record and "Mold-Maintenance" as category
                // This includes Additional Pictures (default or edited label) and Extra Images
                record = buttonLabel;
                category = 'Mold-Maintenance';
              } else if (buttonLabel === 'Action Images' && context === 'maintenance') {
                // For Add Actions: use Sub Action Images and get IDs from savedMaintenanceActivityData
                record = 'Sub Action Images';
                category = 'Other-Maintenance-Records-Actions';
              } else if (buttonLabel && buttonLabel !== 'Action Images' && context === 'maintenance') {
                // For custom labels (like edited Additional Pictures label) or other maintenance records
                // Use the buttonLabel as the record (this handles edited Additional Pictures labels)
                record = buttonLabel;
                category = 'Mold-Maintenance';
              } else {
                // For other sections: use existing logic
                record = buttonLabel === 'Action Images' ? 'Action Images' : 'Sub Action Images';
                category = 'Other-Maintenance-Records-Actions';
              }
              
              // Get actionImageId and subActionImageId from savedMaintenanceActivityData for Add Actions
              let actionImageId = 0;
              let subActionImageId = 0;
              
              if (buttonLabel === 'Action Images' && context === 'maintenance' && savedMaintenanceActivityData) {
                actionImageId = savedMaintenanceActivityData.actionImageId || 0;
                subActionImageId = savedMaintenanceActivityData.subActionImageId || 0;
                console.log('Using savedMaintenanceActivityData for Add Actions:', {
                  actionImageId,
                  subActionImageId,
                  savedMaintenanceActivityData
                });
              }
              
              const segmentData = {
                moldInfoID: moldInfoID,
                imageUrl: result.relativePath,
                segment: slot.label,
                record: record,
                maintenanceId: currentMaintenanceId,
                title: '',
                description: '',
                category: category,
                actionImageId: actionImageId,
                subActionImageId: subActionImageId
              };
              
              console.log('Calling saveOrUpdateMoldImageSegments with segmentData:', segmentData);
              const segmentResponse = await MoldService.saveUpdateImageSegments(segmentData);
              console.log('saveOrUpdateMoldImageSegments response:', segmentResponse);
            
              // Notify parent about uploaded image segment
              if (onImageSegmentUploaded && typeof onImageSegmentUploaded === 'function') {
                const savedSegment = {
                  ...segmentData,
                  ...(segmentResponse?.response || {}),
                };
                if (!savedSegment.record) {
                  savedSegment.record = segmentData.record;
                }
                if (!savedSegment.category) {
                  savedSegment.category = segmentData.category;
                }
                onImageSegmentUploaded(savedSegment, savedSegment.record);
              }
            } catch (segmentError) {
              console.error('Error saving image segment:', segmentError);
              // Don't fail the entire upload process if segment saving fails
            }
          }
        } catch (err) {
          console.error('Error processing image:', err);
        }
      }
      
      if (uploadedFiles.length > 0) {
        dispatch(showToast({ message: 'All Images Uploaded', type: 'success' }));
      }
      
      setSlotImages({});
      setPendingImages([]);
      onClose();
      onSubmit(uploadedFiles);
        
    } catch (error) {
      console.error('Error processing images:', error);
      dispatch(showToast({ message: 'Failed to process images', type: 'danger' }));
    } finally {
      setIsLoading(false);
    }
  };

            

     // Handle modal close
  const handleClose = () => {
    // Clean up object URLs for uploaded files
    Object.values(slotImages).forEach(images => {
      images.forEach(img => {
        if (img.isUploaded && img.src) {
          URL.revokeObjectURL(img.src);
        }
      });
    });
    
    setSlotImages({});
    setUploadedImageUrls([]);
    setShowUploadArea(false);
    onClose();
  };

  return (
    <>
             <Modal 
               show={show} 
               onHide={handleClose} 
               centered 
               dialogClassName="webcam-modal"
               onDragOver={(e) => e.preventDefault()}
               onDrop={(e) => e.preventDefault()}
             >
        {isLoading && <LoadingSpinner isGlobal={false} />}
      <Modal.Header closeButton>
        <Modal.Title>Capture Images</Modal.Title>
      </Modal.Header>
      <Modal.Body 
        className="webcam-modal-body"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => e.preventDefault()}
      >
        <div className="capture-layout">
          {/* Slots on the left */}
          <div className="slot-list">
            {slots.map(slot => (
              <div
                key={slot.key}
                className={`slot-item ${activeSlot === slot.key ? 'active' : ''}`}
                onClick={() => setActiveSlot(slot.key)}
              >
                <input
                  type="text"
                  className="slot-label-input"
                  value={slot.label}
                  onChange={e =>
                    setSlots(prev =>
                      prev.map(s => (s.key === slot.key ? { ...s, label: e.target.value } : s)),
                    )
                  }
                />
              </div>
            ))}

            <Button size="sm" variant="outline-primary" className="mt-2" onClick={addSlot}>
              + Add Segment
            </Button>
          </div>

          {/* Webcam on the right */}
          <div className="webcam-container">
            <Webcam
              key={facingMode}
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              width={500}
              height={350}
              videoConstraints={videoConstraints}
            />
          </div>
        </div>

        <div className="action-buttons">
          <Button 
            size="sm" 
            variant="secondary" 
            onClick={flipCamera} 
            className="action-btn"
            disabled={isCameraLoading}
          >
            {isCameraLoading ? (
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            ) : (
              <FaSyncAlt />
            )}
            {isCameraLoading ? 'Switching Camera...' : 'Flip Camera'}
          </Button>
          <Button size="sm" variant="primary" onClick={captureImage} className="action-btn">
            <FaCamera />
            Capture Photo
          </Button>
          <Button 
            size="sm" 
            variant="success" 
            onClick={() => setShowUploadArea(!showUploadArea)} 
            className="action-btn"
          >
            <FaUpload />
            Upload Images
          </Button>
        </div>

        {/* Upload area */}
        {showUploadArea && (
          <div className="upload-area mt-3">
            <div 
              className="upload-zone"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <label htmlFor="file-upload-input" className="upload-label">
                <FaUpload className="upload-icon" />
                <div className="upload-text">
                  <span className="upload-bold">Click to upload</span> or drag and drop
                </div>
                <div className="upload-subtext">PNG, JPG, GIF, HEIC up to 10MB</div>
                <input
                  id="file-upload-input"
                  type="file"
                  className="hidden-input"
                  accept="image/*,.heic,.heif"
                  multiple
                  onChange={handleFileSelect}
                />
              </label>
            </div>
          </div>
        )}


        {/* Captured and uploaded images grid grouped by slot */}
        <div className="captured-grid mt-3 w-100">
          {slots.map(slot => {
            const images = slotImages[slot.key] || [];
            if (images.length === 0) return null;
            return (
              <div key={slot.key} className="slot-grid">
                <h6>{slot.label}</h6>
                <div className="slot-images">
                  {images.map(img => (
                    <div key={img.id} className="thumbnail">
                      <img 
                        src={img.src} 
                        alt={slot.label} 
                        className="thumbnail-img" 
                        onClick={() => handleEditImage(slot.key, img)}
                        style={{ cursor: 'pointer' }}
                        title="Click to edit image"
                      />
                      <span
                        onClick={() => toggleSelect(slot.key, img.id)}
                        className="thumbnail-check"
                      >
                        {img.isSelected ? (
                          <FaCheckSquare color="green" />
                        ) : (
                          <FaRegSquare color="gray" />
                        )}
                      </span>
                      <button
                        className="remove-thumbnail-btn"
                        onClick={() => removeImage(slot.key, img.id)}
                        title="Remove image"
                      >
                        <FaTimes />
                      </button>
                      {img.isEdited && (
                        <span className="edited-badge" title="Image has been edited">
                          ✓ Edited
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Modal.Body>
      
      {/* Processing Images Loader Overlay */}
      {isProcessingImages && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
            backdropFilter: 'blur(4px)'
          }}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '40px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              textAlign: 'center',
              minWidth: '300px',
              maxWidth: '500px'
            }}
          >
            <div 
              className="spinner-border text-primary" 
              role="status"
              style={{
                width: '80px',
                height: '80px',
                borderWidth: '8px',
                marginBottom: '20px'
              }}
            >
              <span className="visually-hidden">Processing...</span>
            </div>
            <h4 style={{ marginBottom: '15px', color: '#333', fontWeight: '600' }}>
              Processing Images
            </h4>
            <p style={{ color: '#666', marginBottom: '20px', fontSize: '16px' }}>
              {processingProgress.current} of {processingProgress.total} images processed
            </p>
            <div 
              className="progress" 
              style={{ 
                height: '12px', 
                borderRadius: '6px',
                backgroundColor: '#e9ecef',
                overflow: 'hidden'
              }}
            >
              <div 
                className="progress-bar progress-bar-striped progress-bar-animated bg-primary" 
                role="progressbar"
                style={{ 
                  width: `${(processingProgress.current / processingProgress.total) * 100}%`,
                  height: '100%',
                  transition: 'width 0.3s ease'
                }}
                aria-valuenow={processingProgress.current}
                aria-valuemin="0"
                aria-valuemax={processingProgress.total}
              >
                {Math.round((processingProgress.current / processingProgress.total) * 100)}%
              </div>
            </div>
            <p style={{ color: '#999', marginTop: '15px', fontSize: '14px' }}>
              Please wait while we convert and compress your images...
            </p>
          </div>
        </div>
      )}
      
      <Modal.Footer>
                 <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={handleSubmit}
          disabled={
            Object.values(slotImages)
              .flat()
              .filter(i => i.isSelected).length === 0 || isLoading
          }
        >
          {isLoading ? (
            <>
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              Uploading Images...
            </>
          ) : (
            'Save Selected Images'
          )}
        </Button>
      </Modal.Footer>
    </Modal>

    {/* Confirmation Dialog */}
    <Modal show={showConfirmDialog} onHide={() => setShowConfirmDialog(false)} centered size="sm">
      <Modal.Header closeButton>
        <Modal.Title>Confirm Save</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>Do you want to save this {(() => {
          console.log('Confirmation dialog - context:', context);
          console.log('Confirmation dialog - issueData:', issueData);
          return context === 'other-maintenance-issue' ? 'Issue' : context === 'receiving' ? 'mold receiving' : context === 'release' ? 'mold release' : context === 'machine' ? 'machine maintenance' : 'maintenance';
        })()}?</p>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-end">
        <Button variant="primary" onClick={() => handleConfirmSubmit(pendingImages)}>
          Yes
        </Button>
        <Button variant="secondary"  onClick={() => setShowConfirmDialog(false)}>
          No
        </Button>
      </Modal.Footer>
    </Modal>

    {/* Image Editor Modal */}
    {editingImage && (
      <ZoomableImageModal
        show={showImageEditor}
        onHide={() => {
          setShowImageEditor(false);
          setEditingImage(null);
          setEditingSlot(null);
        }}
        imageSrc={editingImage.src}
        imageAlt="Captured Image"
        enableEditor={true}
        onSaveEdited={handleSaveEditedImage}
      />
    )}
    </>
  );
};

export default SegmentWebcamMultiCaptureModal;
